import type { ComposedLine, GlyphRun } from '@proofroom/schema';
import { applyCapitalization } from './lines';

export interface RunProps {
  key: string;
  x: number;
  y: number;
  /** absolute x per character; the browser positions every glyph, composing nothing */
  xs: number[];
  text: string;
  fontFamily: string;
  fontSize: number;
  fill: string;
  /** small caps are left to the browser: the engine scaled the glyphs, we cannot per character */
  fontVariant?: string;
}

/** x position of each glyph origin from the run start and its advances. */
export function glyphOrigins(run: Pick<GlyphRun, 'x' | 'advances'>): number[] {
  const out: number[] = [];
  let x = run.x;
  for (const adv of run.advances) { out.push(x); x += adv; }
  return out;
}

/**
 * Per-character x list for a run. Glyph clusters are relative to the line start, so `runOffset`
 * is the run's first character index within the line. Characters absorbed into a ligature are
 * spread evenly across the ligature's advance so the browser still draws every letter.
 */
export function charPositions(run: GlyphRun, runOffset: number): number[] {
  const n = run.text.length;
  if (n === 0) return [];
  const origins = glyphOrigins(run);
  if (origins.length === 0 || !run.clusters || run.clusters.length !== origins.length) {
    return [run.x];
  }
  const xs = new Array<number>(n).fill(NaN);
  for (let g = 0; g < origins.length; g++) {
    const c = run.clusters[g] - runOffset;
    if (c >= 0 && c < n && Number.isNaN(xs[c])) xs[c] = origins[g];
  }
  // fill absorbed characters by interpolating between the neighbours that do have glyphs
  let i = 0;
  while (i < n) {
    if (!Number.isNaN(xs[i])) { i++; continue; }
    let j = i;
    while (j < n && Number.isNaN(xs[j])) j++;
    const left = i > 0 ? xs[i - 1] : run.x;
    const leftAdvance = i > 0 ? advanceAt(run, run.clusters, i - 1 + runOffset) : 0;
    const right = j < n ? xs[j] : left + leftAdvance;
    const count = j - i + 1;
    for (let k = i; k < j; k++) xs[k] = left + ((right - left) * (k - i + 1)) / count;
    i = j;
  }
  return xs;
}

/** The engine draws a paragraph bullet as a run of its own with clusters [-1]: it is not in the paragraph text, so it takes no character offset. */
export const isMarkerRun = (run: Pick<GlyphRun, 'clusters'>): boolean => run.clusters.length > 0 && run.clusters.every((c) => c < 0);

function advanceAt(run: GlyphRun, clusters: number[], cluster: number): number {
  const g = clusters.indexOf(cluster);
  return g >= 0 ? run.advances[g] : 0;
}

/**
 * What a run draws, and where. The engine shaped the capitalized text but keeps `text` as the
 * source characters, because the line's offsets and every selection are in source coordinates,
 * so the transform is applied here. It is per code point, as the engine's is, and a character
 * whose uppercase is longer than itself (allCaps `stra\u00dfe` shapes as STRASSE) is laid on its
 * own glyph origins rather than pushing everything after it along.
 */
export function drawnRun(run: GlyphRun, runOffset: number): { text: string; xs: number[] } {
  const xs = charPositions(run, runOffset);
  const cap = run.capitalization ?? 'normal';
  const text = applyCapitalization(run.text, cap);
  if (text.length === run.text.length) return { text, xs };
  const origins = glyphOrigins(run);
  const end = origins.length ? origins[origins.length - 1] + run.advances[origins.length - 1] : run.x;
  const out: number[] = [];
  let c = 0;
  for (const ch of run.text) {
    const up = applyCapitalization(ch, cap);
    if (up.length === ch.length) {
      for (let k = 0; k < ch.length; k++) out.push(xs[c + k] ?? run.x);
    } else {
      const own = origins.filter((_, g) => run.clusters[g] - runOffset === c);
      const from = xs[c] ?? run.x;
      const to = xs[c + ch.length] ?? end;
      for (let k = 0; k < up.length; k++) out.push(own.length === up.length ? own[k] : from + ((to - from) * k) / up.length);
    }
    c += ch.length;
  }
  return { text, xs: out };
}

/** One <text> per GlyphRun of a composed line. */
export function composedRunProps(line: ComposedLine): RunProps[] {
  const out: RunProps[] = [];
  let offset = 0;
  line.runs.forEach((run, i) => {
    if (run.text.length > 0) {
      const drawn = isMarkerRun(run) ? { text: run.text, xs: [run.x] } : drawnRun(run, offset);
      out.push({
        key: `${line.paraId}:${line.start}:${i}`,
        x: run.x,
        y: line.baseline,
        xs: drawn.xs,
        text: drawn.text,
        fontFamily: run.fontId,
        fontSize: run.pointSize,
        fill: run.fillHex || '#000000',
        fontVariant: run.capitalization === 'smallCaps' ? 'small-caps' : undefined,
      });
    }
    if (!isMarkerRun(run)) offset += run.text.length;
  });
  return out;
}

/** Horizontal extent of the characters [start, end) of a composed line, for selection highlights. */
export function composedRangeExtent(line: ComposedLine, start: number, end: number): { x0: number; x1: number } | undefined {
  const s = Math.max(start, line.start) - line.start;
  const e = Math.min(end, line.end) - line.start;
  if (e <= s) return undefined;
  let offset = 0;
  let x0: number | undefined, x1: number | undefined;
  for (const run of line.runs) {
    if (isMarkerRun(run)) continue;
    const xs = charPositions(run, offset);
    const origins = glyphOrigins(run);
    const endX = origins.length ? origins[origins.length - 1] + run.advances[run.advances.length - 1] : run.x;
    for (let c = 0; c < run.text.length; c++) {
      const idx = offset + c;
      if (idx >= s && idx < e) {
        const cx = xs[c] ?? run.x;
        const nx = c + 1 < xs.length ? xs[c + 1] : endX;
        x0 = x0 === undefined ? cx : Math.min(x0, cx);
        x1 = x1 === undefined ? nx : Math.max(x1, nx);
      }
    }
    offset += run.text.length;
  }
  return x0 !== undefined && x1 !== undefined ? { x0, x1 } : undefined;
}
